<script setup lang="ts">
  import { computed } from 'vue';
  import { RouterLink, type RouterLinkProps } from 'vue-router';
  import { components, formElements } from '@/cypaths';
  import type { ButtonType } from '@/types';

  defineOptions({ name: 'ElRouterLink' });

  const emits = defineEmits<{
    click: [e: Event];
  }>();

  const { disabled = false, ...props } = defineProps<
    RouterLinkProps & {
      altText?: string;
      matchExact?: boolean;
      primary?: boolean;
      secondary?: boolean;
      slim?: boolean;
      disabled?: boolean;
    }
  >();

  const buttonDisplayStyle = computed<ButtonType>(() =>
    props.primary ? 'primary' : props.secondary ? 'secondary' : 'tertiary');

  const isInert = computed<'true' | undefined>(() => disabled ? 'true' : undefined);

  const cyAttribute = computed<string>(() => ({
    primary: formElements.BUTTON_PRIMARY,
    secondary: formElements.BUTTON_SECONDARY,
    tertiary: components.APP_LINK,
  }[buttonDisplayStyle.value]));

  function handleClick(event: MouseEvent) {
    emits('click', event);
  }
</script>

<template>
  <RouterLink
    :data-cy="cyAttribute"
    :inert="isInert"
    v-bind="$props"
    class="el-router-link box-border inline-flex flex-row gap-2"
    :class="[
      'el-router-link--button',
      {
        'el-router-link--button-primary': buttonDisplayStyle === 'primary',
        'text-center items-center justify-center gap-2_5 rounded-button bg-black leading-none text-white no-underline outline-offset-4 hover:bg-[#333] active:bg-[#333]': buttonDisplayStyle === 'primary',
        'items-center bg-gray-light! font-bold': buttonDisplayStyle === 'primary' && isInert,
        'w-full px-1 md:px-10 md:w-fit md:min-w-[200px] md:max-w-[400px]': buttonDisplayStyle === 'primary' && $slots.default,
        'min-h-12 py-[.8125rem]': buttonDisplayStyle === 'primary' && !slim && $slots.default,
        'min-h-10 py-[.6875rem] leading-[1.125rem]': buttonDisplayStyle === 'primary' && slim && $slots.default,
        'size-12': buttonDisplayStyle === 'primary' && !slim && !$slots.default,
        'size-10': buttonDisplayStyle === 'primary' && slim && !$slots.default,
      },
      {
        'el-router-link--button-secondary': buttonDisplayStyle === 'secondary',
        'text-center items-center justify-center gap-2_5 rounded-button border border-black bg-transparent leading-none text-black no-underline outline-offset-4 hover:bg-[#fafafa]': buttonDisplayStyle === 'secondary',
        'border-gray-light bg-[#fafafa] text-gray-medium ': buttonDisplayStyle === 'secondary' && isInert,
        'w-full px-1 md:px-10 md:w-fit md:min-w-[200px] md:max-w-[400px]': buttonDisplayStyle === 'secondary' && $slots.default,
        'min-h-12 py-[.8125rem]': buttonDisplayStyle === 'secondary' && !slim && $slots.default,
        'min-h-10 md:py-2_5 leading-[1.125rem]': buttonDisplayStyle === 'secondary' && slim && $slots.default,
        'size-12': buttonDisplayStyle === 'secondary' && !slim && !$slots.default,
        'size-10': buttonDisplayStyle === 'secondary' && slim && !$slots.default,
      },
      {
        'el-router-link--tertiary': buttonDisplayStyle === 'tertiary',
        'min-h-10 items-center bg-transparent text-blue-accessible group-[.colored-bg]:text-black underline hover:no-underline md:min-h-auto': buttonDisplayStyle === 'tertiary',
        'text-gray-disabled no-underline': buttonDisplayStyle === 'tertiary' && isInert,
        'size-10 rounded hover:bg-gray-extralight active:bg-gray-extralight md:hover:bg-transparent md:active:bg-transparent': buttonDisplayStyle === 'tertiary' && !$slots.default,
      },
    ]"
    :aria-current-value="to === '' ? 'false' : ariaCurrentValue"
    @click="handleClick"
  >
    <slot name="prefix-icon" />
    <slot />
    <slot name="suffix-icon" />
  </RouterLink>
</template>
